import { ColoursObj, MobileAppFrame } from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useEffect, useRef } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { NotFoundPage } from './feat/404/404'
import { AccountRouter } from './feat/account/accountRouter'
import { AuthenticationRouter } from './feat/authentication/AuthenticationRouter'
import { EditItineraryRoutes } from './feat/editItinerary/EditItineraryRoutes'
import { useGuestStore } from './feat/guest/guest.store'
import { GuestDupRouter } from './feat/guest/guestDup.Routes'
import { HomeRoutes } from './feat/home/homeRoutes'
import { NavBar } from './feat/layout/navBar'
import { OAuthRedirect } from './feat/oauth/pages/oauthRedirect'
import { ChatRouter } from './feat/rocketchat/chatRouter'
import { CreateGroupChannel } from './feat/rocketchat/Components/createGroupChannel'
import { RocketChatMenu } from './feat/rocketchat/rocketChatMenu'
import { WelcomePage } from './feat/welcome/welcomePage'
import { HomeScreen } from './home/HomeScreen'
import { AddTeamMember } from './home/overlay/AddTeamMember'
import { TeamPage } from './home/team/TeamPage'
import { TeamSearchPage } from './home/team/TeamSearchPage'
import { useBoundStore } from './state/bound.store'
import { FilterProvider } from './state/FilterProvider'
import { homeTabSignal } from './state/itineraryState/itinerarySignals'
import { RouteGuardProvider } from './state/routeGuardProvider'
import { useScrollContext } from './state/scrollState/scrollContext'
import { scrollRef } from './state/scrollState/scrollSignals'
import { useEphemeralStore } from './state/useEphemeralStore'
import {
    backgroundImageUrl,
    keyboardState,
    windowSizes,
} from './state/windowState/windowSignals'
import { isWebPreview } from './utils'
import { ItemListRouter } from './v2/pages/itemLists/router'
import { ItemRouter } from './v2/pages/router'

const startingInnerHeight = window.innerHeight

export const AppRoutes = () => {
    const scrollBehavior = useScrollContext(useShallow((s) => s.scrollBehavior))

    const { pathname } = useLocation()

    const { hasToken } = useBoundStore(
        useShallow((s) => {
            return {
                hasToken: s.IDtoken !== undefined,
            }
        })
    )

    const { mapIsFullscreen } = useEphemeralStore(
        useShallow((s) => {
            return {
                mapIsFullscreen: s.mapIsFullscreen,
            }
        })
    )

    scrollRef.value = useRef(null)

    const expandMap =
        mapIsFullscreen && pathname === '/home' && homeTabSignal.value === 'map'

    useEffect(() => {
        document.body.style.backgroundColor = ColoursObj.backgrounds.primary
    }, [])

    return (
        <RouteGuardProvider>
            <MobileAppFrame
                bottomSafeArea={
                    (windowSizes.value.insets?.insets.bottom ?? '0') + 'px'
                }
                topSafeArea={`${windowSizes.value.insets?.insets.top}px`}
                height={
                    ['willShow', 'didShow'].includes(
                        keyboardState.value.openState
                    )
                        ? startingInnerHeight -
                          (keyboardState.value.info?.keyboardHeight ?? 0) +
                          'px'
                        : startingInnerHeight + 'px'
                }
                width="100vw"
                passedRef={scrollRef.value}
                overscrollBehaviour="inherit"
                scrollStyle={{
                    scrollBehavior: scrollBehavior,
                }}
                backgroundImageUrl={backgroundImageUrl.value}
            >
                <Stack>
                    <Routes>
                        <Route
                            element={
                                <GuardedRoute
                                    auth={!hasToken}
                                    to={Paths.home}
                                />
                            }
                        >
                            <Route path="/" element={<WelcomePage />} />
                        </Route>
                        <Route
                            element={
                                <GuardedRoute
                                    auth={
                                        hasToken ||
                                        useGuestStore.getState().isGuestMode
                                    }
                                    to="/authentication/signin"
                                />
                            }
                        >
                            <Route path="tabs/*" element={<HomeRoutes />}>
                                <Route
                                    key={'home'}
                                    path={'home'}
                                    element={<HomeScreen />}
                                />
                                <Route
                                    key={'team'}
                                    path={'team'}
                                    element={
                                        <FilterProvider>
                                            <TeamPage />
                                        </FilterProvider>
                                    }
                                />

                                <Route
                                    key={'rocket'}
                                    path={'rocketchat'}
                                    element={<RocketChatMenu />}
                                />
                            </Route>

                            <Route
                                path="/home"
                                element={<Navigate replace to={Paths.home} />}
                            />
                            <Route
                                path="/edit/*"
                                element={<EditItineraryRoutes />}
                            />
                            <Route path="/item/*" element={<ItemRouter />} />
                            <Route
                                path="/itemList/*"
                                element={<ItemListRouter />}
                            />
                            <Route
                                path="/account/*"
                                element={<AccountRouter />}
                            />
                            <Route
                                path={Paths.teamSearch}
                                element={<TeamSearchPage />}
                            />
                        </Route>
                        <Route
                            element={
                                <GuardedRoute
                                    auth={hasToken}
                                    to="/authentication/signin"
                                />
                            }
                        >
                            <Route
                                path="/rocketChat/*"
                                element={<ChatRouter />}
                            />
                        </Route>

                        <Route
                            path={'/tabs/rocketChat/createGroupChannel'}
                            element={<CreateGroupChannel />}
                        />

                        {!isWebPreview && (
                            <Route
                                key="6"
                                path="/authentication/*"
                                element={<AuthenticationRouter />}
                            />
                        )}

                        <Route
                            key="7"
                            path="/Signup"
                            element={
                                <Navigate to="/authentication/signUp" replace />
                            }
                        />

                        <Route
                            key="8"
                            path="/oauth/callback"
                            element={<OAuthRedirect />}
                        />

                        <Route
                            key="add-users"
                            path="/Setup/add-users"
                            element={<AddTeamMember />}
                        />

                        <Route
                            key="guestdup"
                            path="/guestdup/*"
                            element={<GuestDupRouter />}
                        />

                        <Route key="9" path="*" element={<NotFoundPage />} />
                    </Routes>
                    {!expandMap && <NavBar />}
                </Stack>
            </MobileAppFrame>
        </RouteGuardProvider>
    )
}

interface GuardedRouteProps {
    auth: boolean
    to: string
}

export const GuardedRoute = ({ auth, to }: GuardedRouteProps) => {
    if (auth === true) return <Outlet />
    return <Navigate replace to={to} />
}

export const Paths = {
    home: '/tabs/home',
    team: '/tabs/team',
    rocketChat: '/tabs/rocketChat',
    editGeneralActivity: (activityID: string) =>
        `/edit/activities/general/${activityID}`,
    itemList: (sectionID: number, categoryID?: string) =>
        `/itemList/${sectionID}` + (categoryID ? `/${categoryID}` : ''),
    teamSearch: '/team/search',
}
